import { ThingEventSnapshot } from '@eagle/core-data-types';
import { DtcMatchTypes, DtcTypes, FaultUpdate } from '@eagle/data-function-types';
import { Box, Skeleton, Typography } from '@ethings/ui-toolkit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isNewEvent, useDtcFaultDescriptionQuery, useDtcSourceDescriptionQuery } from '../events-table/events-table.queries';
import { useBatchFaultData, useBatchFaultDataSafe } from './batch-fault-context';

export interface NormalizedFaultEventItem {
  event: ThingEventSnapshot;
  originalEvents: ThingEventSnapshot[];
  firstEventOccurred: Date;
  lastEventOccurred: Date;
  isOngoing: boolean;
}

interface FaultDescriptionProps {
  eventData: FaultUpdate;
  thingId: string;
}

export const FaultDescription: FC<FaultDescriptionProps> = ({ eventData, thingId }) => {
  const batchContext = useBatchFaultDataSafe();

  if (eventData.type === DtcTypes.HINO) {
    return null;
  }

  const brandVariantId = isNewEvent(eventData) ? eventData.dtcBrandVariantId : null;

  if (batchContext?.isLoading) {
    return <Skeleton />;
  }

  const hasBatchData = brandVariantId && batchContext && batchContext.faultDescriptions[brandVariantId] !== undefined;

  return (
    <>
      {hasBatchData ? (
        <J1939FaultDescriptionWithBatch brandVariantId={brandVariantId} />
      ) : (
        <J1939FaultDescriptionWithApi eventData={eventData} thingId={thingId} />
      )}
    </>
  );
};

const J1939FaultDescriptionWithApi: FC<{ eventData: FaultUpdate; thingId: string }> = ({ eventData, thingId }) => {
  const faultDescriptionQuery = useDtcFaultDescriptionQuery({ eventData, thingId });

  if (faultDescriptionQuery.status === 'pending') {
    return <Skeleton />;
  }

  if (faultDescriptionQuery.status === 'error' || !faultDescriptionQuery.data) {
    return null;
  }
  const description = faultDescriptionQuery.data.description;

  if (!description) return null;

  const shouldAddPrefix = (faultDescriptionQuery.data && 'matchType' in faultDescriptionQuery.data && faultDescriptionQuery.data.matchType === DtcMatchTypes.J1939);

  return shouldAddPrefix ? `J1939 - ${description}` : description;
};

const J1939FaultDescriptionWithBatch: FC<{ brandVariantId: number }> = ({ brandVariantId }) => {
  const { faultDescriptions, isLoading } = useBatchFaultData();

  const description = useMemo(() => {
    if (isLoading) {
      return null;
    }

    const faultDescription = faultDescriptions[brandVariantId];
    return faultDescription?.description || null;
  }, [faultDescriptions, brandVariantId, isLoading]);

  if (isLoading) {
    return <Skeleton />;
  }

  if (!description) {
    return null;
  }

  return description;
};

const FaultSourceDescriptionLabels: FC<{ systemLabel: string; faultCode: string; spn: string; fmi: string | number }> = ({
  systemLabel,
  faultCode,
  spn,
  fmi,
}) => {
  const { t } = useTranslation(['track']);

  return (
    <Box data-testid="fault-description-container" display="flex" flexDirection="column" width={1}>
      <Typography variant="caption" data-testid="fault-system-label">
        {systemLabel}
      </Typography>
      <Typography variant="caption" data-testid="fault-code-label">
        {t('common:event-descriptions-v3.fault.dtc.faultCode.labels')}: {faultCode}
      </Typography>
      <Typography variant="caption" color="text.secondary" fontSize="0.65rem" data-testid="fault-spn-fmi-label">
        {t('common:event-descriptions-v3.fault.dtc.spn.labels')}: {spn} - FMI: {fmi}
      </Typography>
    </Box>
  );
};

export const FaultSourceDescription: FC<FaultDescriptionProps> = ({ eventData, thingId }) => {
  const batchContext = useBatchFaultDataSafe();
  const sourceId = isNewEvent(eventData) ? eventData.dtcSourceId : null;
  const brandVariantId = isNewEvent(eventData) ? eventData.dtcBrandVariantId : null;

  if (batchContext?.isLoading) {
    return <Skeleton width="100%" />;
  }

  const hasSourceData = sourceId && batchContext && batchContext.sourceDescriptions[sourceId] !== undefined;

  return (
    <>
      {sourceId && brandVariantId && hasSourceData ? (
        <FaultSourceDescriptionWithBatch sourceId={sourceId} brandVariantId={brandVariantId} eventData={eventData} />
      ) : (
        <FaultSourceDescriptionWithApi eventData={eventData} thingId={thingId} />
      )}
    </>
  );
};

const FaultSourceDescriptionWithApi: FC<FaultDescriptionProps> = ({ eventData, thingId }) => {
  const { t } = useTranslation(['track']);

  const sourceQuery = useDtcSourceDescriptionQuery({ eventData, thingId });
  const faultQuery = useDtcFaultDescriptionQuery({ eventData, thingId });

  if (sourceQuery.status === 'pending' || faultQuery.status === 'pending') {
    return <Skeleton width="100%" />;
  }

  const systemLabel = sourceQuery.data?.description || `SA: ${eventData?.sourceAddress ?? t('track:page.thing-map.state.unknown')}`;
  const faultCodeFromApi = faultQuery.data ? ('faultCode' in faultQuery.data ? faultQuery.data.faultCode : undefined) : undefined;
  const faultCode = eventData?.faultCode ?? faultCodeFromApi ?? t('track:page.thing-map.state.unknown');
  const spn = eventData?.code ? eventData.code : t('track:page.thing-map.state.unknown');
  const fmi = eventData?.failureModeIdentifier ? eventData.failureModeIdentifier : t('track:page.thing-map.state.unknown');

  return <FaultSourceDescriptionLabels systemLabel={systemLabel} faultCode={faultCode} spn={spn} fmi={fmi} />;
};

const FaultSourceDescriptionWithBatch: FC<{ sourceId: number; brandVariantId: number; eventData: FaultUpdate }> = ({ sourceId, brandVariantId, eventData }) => {
  const { t } = useTranslation(['track']);
  const { faultDescriptions, sourceDescriptions, isLoading } = useBatchFaultData();

  if (isLoading) {
    return <Skeleton width="100%" />;
  }
  const sourceDescription = sourceDescriptions[sourceId];
  const faultDescription = faultDescriptions[brandVariantId];
  const systemLabel = sourceDescription?.description || `SA: ${eventData?.sourceAddress ?? t('track:page.thing-map.state.unknown')}`;
  const faultCodeFromApi = faultDescription && 'faultCode' in faultDescription ? String(faultDescription.faultCode) : undefined;
  const faultCode = eventData?.faultCode ?? faultCodeFromApi ?? t('track:page.thing-map.state.unknown');
  const spn = eventData.code ? eventData.code : t('track:page.thing-map.state.unknown');
  const fmi = eventData?.failureModeIdentifier ? eventData.failureModeIdentifier : t('track:page.thing-map.state.unknown');

  return <FaultSourceDescriptionLabels systemLabel={systemLabel} faultCode={faultCode} spn={spn} fmi={fmi} />;
};
